import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Image, List, message, Typography, Tooltip, Divider, Tag, Modal, Upload } from 'antd';
import {
    DeleteOutlined,
    FileOutlined,
    CheckCircleTwoTone,
    LoadingOutlined,
    CheckOutlined,
    SyncOutlined,
    CloseCircleOutlined
} from '@ant-design/icons';
import { convertToKB, isAllSuccess } from '../../exports/util';
import './upload-file.css';
import portalAPI from '../../api/api';
import { UPLOAD_CONTENT_URL, UPLOAD_DOCS_TO_CLAIM_URL } from '../../api/URLs';
import _, { debounce } from 'lodash';
import LilyButton from '../button/lily-button';
import { imageCompressor } from '@mbs-dev/react-image-compressor';
import { bytesToMB, isImageType, MAX_FILE_SIZE_MB } from '../../exports/upload.util';

const { Dragger } = Upload;
const { Text } = Typography;

const UploadDocModal = (props) => {
    const { caseNumber, onPing } = props;
    const [visible, setVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [fileList, setFileList] = useState([]);

    const renderPreview = (item) => {
        if (item?.type?.startsWith('image')) {
            return <Image src={item.url} width={100} />;
        }
        return (
            <Tooltip title="Click to view file">
                <a href={item.url} target="_blank" rel="noreferrer">
                    <FileOutlined style={{ fontSize: 20 }} /> {item?.name}
                </a>
            </Tooltip>
        );
    };

    const renderStatusIcon = (status) => {
        switch (status) {
            case "done":
                return <CheckCircleTwoTone twoToneColor="#52c41a" />;
            case "uploading":
                return <LoadingOutlined />;
            case "error":
                return <CloseCircleOutlined />;
            default:
                return null;
        }
    };

    const renderTagStatus = (status) => {
        switch (status) {
            case "done":
                return <Tag color="success">Completed</Tag>;
            case "uploading":
                return <Tag icon={<SyncOutlined spin />} color="processing">Uploading</Tag>;
            case "error":
                return <Tag icon={<CloseCircleOutlined />} color="error">Error</Tag>;
            default:
                return null;
        }
    };

    const handleRemove = (uidToRemove) => {
        const newList = fileList.filter(file => file.uid !== uidToRemove);
        setFileList(newList);

        message.info('File removed.');
    };

    const uploadProps = {
        name: 'file',
        multiple: true,
        maxCount: 5,
        action: portalAPI.defaults.baseURL + UPLOAD_CONTENT_URL,
        showUploadList: false,
        accept: '.png,.jpg,.jpeg,.pdf,.doc,.docx',
        fileList,
        withCredentials: true,
        beforeUpload: async (file) => {
            const isImage = isImageType(file.type);
            let fileSize = bytesToMB(file.size);

            const isOver2MB = fileSize > MAX_FILE_SIZE_MB;

            let QUALITY = 1.2 / fileSize;

            if (isImage && isOver2MB) {
                try {
                    const compressedBlob = await imageCompressor(file, QUALITY);

                    return new File([compressedBlob], file.name, {
                        type: compressedBlob.type,
                        lastModified: Date.now(),
                    });
                } catch (error) {
                    console.error('Compression failed:', error);
                    message.error('Image compression failed');
                    return Upload.LIST_IGNORE;
                }
            }

            return file;
        },
        onChange(info) {
            const { file, fileList: newFileList } = info;
            const responseData = file.response;

            if (file.status === 'removed') return;

            if (file.status === 'uploading') {
                setIsLoading(true);
                setFileList(newFileList);
            }

            if (responseData?.status === 400) {
                const newList = fileList.filter(item => item.uid !== file.uid);
                setFileList(newList);
                setIsLoading(false);
                let errMessage = responseData?.message === "Uploaded file is too big" ? "Upload failed – maximum file size 5MB." : responseData.message;
                message.error(errMessage);
                return false;
            }

            if (file.status === "done") {
                setIsLoading(false);

                let fileData = {
                    id: Date.now() + Math.random(),
                    fileName: file.name,
                    s3Key: responseData?.data?.s3key,
                    fileType: file.type,
                    url: responseData.data.url,
                    presignedUrl: responseData.data.presignedUrl,
                    contentDocumentId: responseData?.data?.contentDocumentId || '',
                };

                const updatedFileList = newFileList.map((item) => {
                    if (item.uid === file.uid) {
                        return {
                            ...item,
                            status: responseData?.status !== 200 ? "error" : "done",
                            url: responseData?.data?.presignedUrl,
                            urlAccess: responseData?.data?.url,
                            fileData: fileData,
                        };
                    }
                    return item;
                });

                setFileList(updatedFileList);

                if (responseData?.status === 200) {
                    message.success(`${file.name} uploaded successfully.`);
                } else {
                    message.error(`${file.name} failed to upload.`);
                }
            }

            if (file.status === "error") {
                const updatedFileList = newFileList.map((item) => {
                    if (item.uid === file.uid) {
                        return {
                            ...item,
                            status: responseData?.status !== 200 ? "error" : "done",
                        };
                    }
                    return item;
                });

                setFileList(updatedFileList);
                setIsLoading(false);
                let errorMessage = responseData?.message || 'upload failed.'
                message.error(`${file.name} ${errorMessage}.`);
            }
        },
    };

    const hasFiles = useMemo(() => fileList.length > 0, [fileList]);

    const handleSave = async () => {
        setSaveLoading(true)
        try {
            if (!caseNumber || caseNumber === '') {
                message.error("Claim ID is required");
                return false
            };

            let body = {}

            if (fileList && fileList.length > 0) {
                body['files'] = fileList.map((item) => {
                    return (
                        item.fileData
                    )
                })
            }
            let URL = UPLOAD_DOCS_TO_CLAIM_URL.replace(":id", caseNumber);
            let rsp = await portalAPI.put(URL, body);
            if (rsp.data.status === 200) {
                setSaveLoading(false);
                setVisible(false);
                setIsLoading(false);
                setFileList([]);
                onPing();
            }
        } catch (error) {
            message.error(`Failed to save documents.`);
        } finally {
            setSaveLoading(false);
        }
    }

    const initState = () => {
        setSaveLoading(false);
        setVisible(false);
        setIsLoading(false);
        setFileList([]);
    }

    const onModalClose = async () => {
        let title, okText, cancelText, onOk, onCancel;
        if (fileList && fileList.length > 0) {
            title = 'Closing now will discard all changes you just made';
            cancelText = 'Stay';
            onOk = () => {
                initState();
                setVisible(false);
                Modal.destroyAll();
            };
            Modal.confirm({
                title: (
                    <p>
                        <div>{title}</div>
                        <Typography.Text type={'secondary'}>Click outside to dismiss this dialog</Typography.Text>
                    </p>
                ),
                okText,
                maskClosable: true,
                footer: () => (
                    <>
                        <Button
                            onClick={() => {
                                (onCancel ? onCancel : () => { })();
                                Modal.destroyAll();
                            }}
                        >
                            {cancelText}
                        </Button>
                        <LilyButton text="Close" style={{ borderRadius: '5px' }} onClick={onOk} />
                    </>
                ),
            });
        } else {
            initState();
            setVisible(false);
        }
    }

    const notAbleToSave = useMemo(() => {
        return isLoading || !fileList.length || !isAllSuccess(fileList);
    }, [fileList, isLoading]);

    return (
        <div>
            <Button className='mt-2' onClick={() => setVisible(true)}>Upload Document</Button>
            <Modal
                title="Upload additional documents"
                open={visible}
                onCancel={onModalClose}
                footer={<div className='d-flex flex-row gap-2 justify-content-end'>
                    <Button onClick={onModalClose} disabled={isLoading}>Cancel</Button>
                    <LilyButton disabled={notAbleToSave} loading={saveLoading} text="Save" style={{ borderRadius: '5px' }} onClick={handleSave} />
                </div>}
                width={800}
            >
                <div className='upload-portal'>
                    <Dragger
                        {...uploadProps}
                        style={{ marginBottom: 16 }}
                        className="custom-upload"
                    >
                        <Button className="upload-btn" icon={<CheckOutlined />}>
                            Choose
                        </Button>
                        <p className="ant-upload-hint">
                            Drag and drop files to here to upload.
                        </p>
                    </Dragger>

                    {hasFiles && (
                        <>
                            <Divider className='m-0' />
                            <List
                                className='uploaded-list'
                                dataSource={fileList}
                                renderItem={(item) => (
                                    <List.Item
                                        actions={[
                                            <Tooltip title="Remove file">
                                                <Button
                                                    icon={<DeleteOutlined />}
                                                    danger
                                                    size="small"
                                                    onClick={() => handleRemove(item.uid)}
                                                />
                                            </Tooltip>
                                        ]}
                                    >
                                        <div className='row'>
                                            <div className='col-4'>{renderPreview(item)}</div>
                                            <div className='col-8'>
                                                <div className='d-flex flex-row align-items-center gap-3'>
                                                    <span>{renderStatusIcon(item.status)}</span>
                                                    <div>
                                                        <div className='d-flex flex-row align-items-center gap-3'>
                                                            <div className='d-flex flex-column align-items-start'>
                                                                <Text className='file-name'>{item.name}</Text>
                                                                <Text>{item.size ? convertToKB(item.size) : 'N/A'}</Text>
                                                            </div>
                                                            {renderTagStatus(item.status)}
                                                        </div>
                                                        {
                                                            item.status === 'error' && (
                                                                <Tag bordered={false} color="error">
                                                                    {item?.response?.message}
                                                                </Tag>
                                                            )
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </List.Item>
                                )}
                            />
                        </>
                    )}
                </div>
            </Modal>
        </div>
    )
}

export default UploadDocModal